import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { BetPlacementService } from "../../../../../../../../shared/src/lib/services/bet-placement.service";
import { CoreService } from "../../../../../../../../shared/src/lib/services/core.service";
import { NavService } from "../../../../../../../../shared/src/lib/services/nav.service";
import { MatIconModule } from "@angular/material/icon";
import { AsyncPipe, DecimalPipe, NgClass } from "@angular/common";
import { OverlayComponent } from "../../../../../components/overlay/overlay.component";
import { Router } from "@angular/router";
import { MarketOpsService } from "../../market/market-ops.service";
import { PnlCalculatorService } from "../../../../../../../../shared/src/lib/services/pnl-calculator.service";
import { isNil } from "lodash";
import { ExRateService } from "../../../../../../../../shared/src/lib/services/ex-rate.service";
import { safeParseFloat } from "../../../../../../../../shared/src/lib/utils/common";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "[app-b-runner-row]",
  imports: [MatIconModule, NgClass, DecimalPipe, OverlayComponent, AsyncPipe],
  templateUrl: "./b-runner-row.component.html",
  styleUrl: "./b-runner-row.component.scss",
})
export class BRunnerRowComponent implements OnInit, OnDestroy, OnChanges {
  @Input() market: any;
  @Input() data: any;
  @Input() update: any = null;
  runnerNetEffect$ = new BehaviorSubject<any>(null);
  runnerPNetEffect$ = new BehaviorSubject<any>(null);
  readonly destroy$ = new Subject<void>();
  row: any = {
    back1: {
      price: 0,
      size: 0,
    },
    back2: {
      price: 0,
      size: 0,
    },
    back3: {
      price: 0,
      size: 0,
    },
    lay1: {
      price: 0,
      size: 0,
    },
    lay2: {
      price: 0,
      size: 0,
    },
    lay3: {
      price: 0,
      size: 0,
    },
  };
  placementService = inject(BetPlacementService);
  coreService = inject(CoreService);
  navService = inject(NavService);
  marketOps = inject(MarketOpsService);
  router = inject(Router);
  priceChangeFlash: boolean = false;
  pnlCalculator = inject(PnlCalculatorService);
  rowStatus: string = "";
  isNil = isNil;
  exRateService = inject(ExRateService);
  flashTimeouts: { [key: string]: any } = {};

  constructor() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes["update"]) {
      const update = changes["update"]?.currentValue;
      this.handleRowChanges(update);
    }
  }

  ngOnInit() {
    // console.log(this.market);
    // console.log(this.data);
    this.initValues();
    this.setFlashingRule();
    this.getNetEffect();
  }

  getNetEffect() {
    this.pnlCalculator
      .getNetEffect(this.market?.topic, this.data?.selectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.runnerNetEffect$.next(result);
      });

    this.pnlCalculator
      .getPotentialNetEffect(this.market?.topic, this.data?.selectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.runnerPNetEffect$.next(result);
      });
  }

  setFlashingRule() {
    const slipSettings = this.placementService.getSlipSettings();
    this.priceChangeFlash = slipSettings?.betSlipSettings?.flashOnPriceChange === "enable";
  }

  initValues() {
    // console.log(this.data);
    this.handleRowChanges(this.data);
  }

  handleRowChanges(updateData: any) {
    if (updateData) {
      this.rowStatus = updateData.status;
      this.updateColumn(updateData, 0);
      this.updateColumn(updateData, 1);
      this.updateColumn(updateData, 2);
    }
  }

  updateColumn(updateData: any, i: number) {
    let b = `back${i + 1}`; // back key
    let l = `lay${i + 1}`; // lay key
    let bc = this.row[b]; // back cell
    let lc = this.row[l]; // lay cell

    const { backPrice, backVolume, layPrice, layVolume } = updateData || {};

    const uBp = Array.isArray(backPrice) ? backPrice : [backPrice]; // updated back price
    const uBv = Array.isArray(backVolume) ? backVolume : [backVolume]; // updated back volume
    const uLp = Array.isArray(layPrice) ? layPrice : [layPrice]; // updated lay price
    const uLv = Array.isArray(layVolume) ? layVolume : [layVolume]; // updated lay volume

    const nbPrice = safeParseFloat(uBp?.[i]) ?? 0; // new back price
    const nbSize = safeParseFloat(uBv?.[i]) ?? 0; // new back size
    const nlPrice = safeParseFloat(uLp?.[i]) ?? 0; // new lay price
    const nlSize = safeParseFloat(uLv?.[i]) ?? 0; // new lay size

    const bChanged = bc.price !== nbPrice || bc.size !== nbSize; // back changed
    const lChanged = lc.price !== nlPrice || lc.size !== nlSize; // lay changed

    bc.changed = bChanged;
    bc.price = nbPrice;
    bc.size = nbSize;
    if (bChanged) {
      clearTimeout(this.flashTimeouts[b]);
      this.flashTimeouts[b] = setTimeout(() => {
        bc.changed = false;
      }, 2000);
    }

    lc.changed = lChanged;
    lc.price = nlPrice;
    lc.size = nlSize;
    if (lChanged) {
      clearTimeout(this.flashTimeouts[l]);
      this.flashTimeouts[l] = setTimeout(() => {
        lc.changed = false;
      }, 2000);
    }
  }

  handleSelect(_data: any) {
    if (_data?.PRICE === 0) return;
    this.navService.openBetSlipBar.next(true);
    const data = {
      ..._data,
      F_ODDS: this.market?.providerId === "0" ? _data?.PRICE : _data?.PRICE / 100 + 1,
      PROVIDER: "SB",
      PROVIDER_ID: this.market?.providerId,
      SERVICE_ID: this.market?.serviceId,
      MARKET_ID: this.market?.marketId,
      SPORT_ID: this.market.eventType?.id,
      EVENT_ID: this.market?.eventId,
      PERSISTENCE_TYPE: this.placementService.persistenceType,
      marketName: this.market?.name,
      eventName: this.market.eventName,
      SELECTION_ID: this.data?.selectionId,
      HANDICAP: this.data?.handicap,
      runnerName: this.data?.runnerName,
      topic: this.market?.topic,
      minBet: this.market?.minBet,
      maxBet: this.market?.maxBet,
      descriptionPrefix: "Bookmaker",
      runners: this.market?.runners,
      context: "bookmaker",
      isMOddsBookmaker: this.market?.isMOddsBookmaker,
    };

    this.placementService.setParams({
      type: "ODDS",
      data,
    });
  }

  get isRowActive() {
    return this.rowStatus === "ACTIVE";
  }

  get isAutoBm() {
    return this.market?.providerId === "0";
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
